/**
 * Compound Management Action Definitions
 *
 * Actions for loading, copying, renaming, removing, assembling,
 * and converting compounds.
 *
 * @module gibbs/registry/actions/compound-mgmt
 */

import { WorkflowTypes as T } from '../types';
import { param, pre, post } from '../schema';

// ─── load-compound ──────────────────────────────────────────────────────────

export const loadCompound = {
    id: 'load-compound',
    name: 'Load Compound into Workspace',
    category: 'Compound Management',
    description: 'Load USER-PROVIDED molecular structures (SMILES, SDF, MOL, PDB files) into the workspace. Requires molecule data as input. To load co-crystallized reference ligands from the PDB entry instead, use load-starter-compounds. To draw a new compound, use open-sketcher.',
    inputs: [
        param('molSource', T.MolData, 'Molecule data with format specification (sdf, mol, mol2, pdb, cif, smi, inchi, cdx, cdxml, cml, xyz)'),
        param('moleculeLoadOptions', 'object', 'Alignment and preprocessing options', false),
        param('caseData', T.CaseData, 'Target protein case'),
    ],
    outputs: [
        param('compounds', T.CompoundList, 'Loaded compounds with 3D coordinates'),
        param('heritage', 'object', 'Provenance tracking for each compound'),
    ],
    parameters: [
        param('alignAction', 'string', 'How to position in binding site', false),
        param('forcefield', 'string', 'Force field for parameterization', false),
        param('maxMolecules', 'number', 'Limit on molecules to load from multi-mol files', false),
        param('skipProperties', 'boolean', 'Skip property calculation on load', false, { defaultValue: false }),
    ],
    dependsOn: [],
    preconditions: [
        pre('protein_loaded', 'A protein must be loaded'),
    ],
    postconditions: [
        post('compound_loaded', 'At least one compound is loaded in the workspace'),
    ],
    runtime: 'short',
    status: 'implemented',
    userAction: 'LoadMolData',
    serverCommand: 'load-mol-data',
    jobType: null,
};

// ─── load-starter-compounds ─────────────────────────────────────────────────

export const loadStarterCompounds = {
    id: 'load-starter-compounds',
    name: 'Load Starter/Reference Compounds',
    category: 'Compound Management',
    description: 'Load reference ligands when the protein was loaded from a NON-PDB source (e.g., load-protein-from-file or load-protein-from-map) and you want to add the canonical co-crystal compounds afterward. Do NOT chain this after load-protein-from-pdb — that action ALREADY brings the crystal ligand into the workspace, so a follow-up load-starter-compounds is redundant from the user perspective. To load your own compounds from SMILES/SDF/files, use load-compound instead.',
    inputs: [
        param('caseData', T.CaseData, 'Active protein case'),
    ],
    outputs: [
        param('compounds', T.CompoundList, 'Reference compounds with heritage tracking'),
    ],
    parameters: [],
    dependsOn: [],
    preconditions: [
        pre('protein_loaded', 'A protein must be loaded with case data'),
    ],
    postconditions: [
        post('compound_loaded', 'Reference compounds are loaded in the workspace'),
    ],
    runtime: 'short',
    status: 'implemented',
    userAction: 'LoadStarterCompounds',
    serverCommand: 'load-starter-compounds',
    jobType: null,
};

// ─── copy-compound ──────────────────────────────────────────────────────────

export const copyCompound = {
    id: 'copy-compound',
    name: 'Duplicate Compound',
    category: 'Compound Management',
    description: 'Create an exact copy of a compound in the workspace. Heritage is tracked as duplicate. Useful for creating variants to modify independently.',
    inputs: [
        param('compound', T.Compound, 'Source compound'),
    ],
    outputs: [
        param('newCompound', T.Compound, 'Duplicated compound with new identity'),
    ],
    parameters: [],
    dependsOn: [],
    preconditions: [
        pre('compound_loaded', 'A compound must be loaded'),
    ],
    postconditions: [
        post('compound_loaded', 'A new compound copy exists in the workspace'),
    ],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'CopyCompound',
    serverCommand: 'copy-compound',
    jobType: null,
};

// ─── rename-compound ────────────────────────────────────────────────────────

export const renameCompound = {
    id: 'rename-compound',
    name: 'Rename Compound',
    category: 'Compound Management',
    description: 'Change the display name of a compound. Checks for name collisions.',
    inputs: [
        param('compound', T.Compound, 'Target compound'),
        param('newName', 'string', 'New display name'),
    ],
    outputs: [],
    parameters: [],
    dependsOn: [],
    preconditions: [
        pre('compound_loaded', 'A compound must be loaded'),
    ],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'RenameCompound',
    serverCommand: 'rename-compound',
    jobType: null,
};

// ─── remove-compound ────────────────────────────────────────────────────────

export const removeCompound = {
    id: 'remove-compound',
    name: 'Remove Compound from Workspace',
    category: 'Compound Management',
    description: 'Delete a compound from the workspace and server.',
    inputs: [
        param('compound', T.Compound, 'Compound to remove'),
    ],
    outputs: [],
    parameters: [],
    dependsOn: [],
    preconditions: [
        pre('compound_loaded', 'A compound must be loaded'),
    ],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'RemoveCompound',
    serverCommand: 'remove-compound',
    jobType: null,
};

// ─── copy-residues-to-compound ──────────────────────────────────────────────

export const copyResiduesToCompound = {
    id: 'copy-residues-to-compound',
    name: 'Convert Residues to Compound',
    category: 'Compound Management',
    description: 'Extract selected protein residues and convert them into a compound object. Useful for studying peptide fragments or specific binding site residues.',
    inputs: [
        param('residues', 'object', 'One or more residues from a single protein'),
    ],
    outputs: [
        param('compound', T.Compound, 'New compound from residue atoms'),
    ],
    parameters: [],
    dependsOn: [],
    preconditions: [
        pre('protein_loaded', 'A protein must be loaded with residues'),
    ],
    postconditions: [
        post('compound_loaded', 'A compound is created from residue atoms'),
    ],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'CopyResiduesToCompound',
    serverCommand: 'copy-compound',
    jobType: null,
};

// ─── assemble-compound ──────────────────────────────────────────────────────

export const assembleCompound = {
    id: 'assemble-compound',
    name: 'Assemble Compound from Fragments',
    category: 'Compound Management',
    description: 'INTERNAL. Low-level API that merges an existing, specific fragment object onto a source compound at user-selected attachment atoms. This action is NOT appropriate for open-ended design requests ("design a lead", "optimize this compound", "generate analogs") — it has no way to pick a fragment or attachment points on its own. It is invoked by interactive fragment-growth tooling after the user selects a fragment in the UI. For open-ended design, use grow-from-all-atoms (enumerative growth from fragment map) or job-reinvent (de novo generation). For lead optimization, start with hit-discovery or dock-and-score followed by iterative growth.',
    inputs: [
        param('modifications', 'object', 'Array of {srcMol, modMol, modInfo} specifying how pieces attach. Every entry MUST carry a concrete modMol fragment object; cannot be synthesized from a text description.'),
        param('caseData', T.CaseData, 'Protein context'),
    ],
    outputs: [
        param('compound', T.Compound, 'Assembled compound'),
    ],
    parameters: [],
    dependsOn: [],
    preconditions: [
        pre('protein_loaded', 'A protein must be loaded'),
        pre('fragment_data_available', 'Fragments or compounds must be loaded'),
    ],
    postconditions: [
        post('compound_loaded', 'An assembled compound is loaded in the workspace'),
    ],
    runtime: 'short',
    status: 'implemented',
    userAction: 'AssembleCompound',
    serverCommand: 'assemble-compound',
    jobType: null,
};

// ─── translate-molecule ─────────────────────────────────────────────────────

export const translateMolecule = {
    id: 'translate-molecule',
    name: 'Convert Molecule Format',
    category: 'Compound Management',
    description: 'Convert a molecule between formats (e.g., SMILES to SDF, SDF to PDB). Server-side conversion using chemistry toolkit.',
    inputs: [
        param('molId', 'string', 'Molecule identifier'),
        param('inType', 'string', 'Input format'),
        param('molData', T.MolData, 'Molecule data'),
        param('outType', 'string', 'Desired output format'),
    ],
    outputs: [
        param('convertedMol', T.MolData, 'Molecule in target format'),
    ],
    parameters: [],
    dependsOn: [],
    preconditions: [],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: null,
    serverCommand: 'translate-molecule',
    jobType: null,
};

// ─── Default Export ─────────────────────────────────────────────────────────

export default [
    loadCompound,
    loadStarterCompounds,
    copyCompound,
    renameCompound,
    removeCompound,
    copyResiduesToCompound,
    assembleCompound,
    translateMolecule,
];
